import * as React from 'react'
import { useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { TableHead } from '@mui/material'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import { SellerState } from '../../../../features/types'

interface Props {
    sellersData: SellerState[]
    isFetchingSellers: boolean
    page: number
    rowsPerPage: number
    pageNumber: number
    totalCount: number
    setPage: (page: number) => void
    setRowsPerPage: (rowsPerPage: number) => void
}

const SellersReportTable: React.FC<Props> = ({
    sellersData = [],
    isFetchingSellers = false,
    page = 0,
    rowsPerPage = 0,
    pageNumber = 0,
    totalCount = 0,
    setPage,
    setRowsPerPage,
}) => {
    const { formatMessage } = useIntl()

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        pageNumber > 0 ? Math.max(0, rowsPerPage - sellersData.length) : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(1)
    }

    return (
        <>
            {sellersData ? (
                <TableContainer
                    component={Paper}
                    sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                    elevation={0}
                >
                    <Table sx={{ minWidth: 500 }}>
                        <TableHead sx={{ bgcolor: 'grey.100' }}>
                            <TableRow>
                                <TableCell>
                                    #
                                </TableCell>
                                <TableCell>
                                    {formatMessage({ id: 'fullName' })}
                                </TableCell>

                                <TableCell align="right">
                                    {formatMessage({ id: 'numOfSales' })}
                                </TableCell>

                                <TableCell align="right">
                                    {formatMessage({ id: 'sumOfSales' })} KZT
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        {sellersData.length ? (
                            <TableBody>
                                {sellersData.map((row, index) => (
                                    <TableRow key={row.id}>
                                        <TableCell>
                                            {pageNumber * rowsPerPage + index + 1}
                                        </TableCell>
                                        <TableCell>
                                            {row.firstName + ' ' + row.lastName}
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.numOfSales}
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.sumOfSales} тг
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {emptyRows > 0 && (
                                    <TableRow
                                        style={{ height: 53 * emptyRows }}
                                    >
                                        <TableCell colSpan={4} />
                                    </TableRow>
                                )}
                            </TableBody>
                        ) : (
                            !isFetchingSellers && (
                                <TableBody>
                                    <TableRow>
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            align="center"
                                            colSpan={4}
                                        >
                                            {formatMessage({ id: 'noData' })}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            )
                        )}

                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    rowsPerPageOptions={[
                                        5,
                                        10,
                                        25,
                                        { label: 'All', value: -1 },
                                    ]}
                                    colSpan={4}
                                    count={totalCount}
                                    rowsPerPage={rowsPerPage}
                                    page={pageNumber}
                                    SelectProps={{
                                        inputProps: {
                                            'aria-label': 'rows per page',
                                        },
                                        native: true,
                                    }}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={
                                        handleChangeRowsPerPage
                                    }
                                    ActionsComponent={TablePaginationActions}
                                    sx={{ border: 0 }}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            ) : (
                <Box sx={{ p: 1 }}>{formatMessage({ id: 'noData' })}</Box>
            )}
        </>
    )
}

export default SellersReportTable
